import type {
	PlanCategory,
	PlanDisplayCategory,
	PlanSourceOrigin,
	PlanStatus,
	ScheduleFacetsOut,
	SystemDomain,
} from '@/client';

/**
 * The schedule's filters, and why they are query params rather than a `useMemo`.
 *
 * The plan endpoint is a **faceted search**: it answers with a page of cards *and* with counts per
 * facet value — `by_category`, `by_display_category`, `by_source_origin`, `by_status` — computed
 * over the set the filters have not been applied to. Facets are what let a control say "Warranty 5" before you press it, so
 * you can see what a filter would give you rather than discovering it by trying.
 *
 * That forces three things, none of them optional:
 *
 * 1. **Filtering is server-side.** `page_info` says the answer is paged, so the client holds one
 *    page and never the set. Filtering a page filters the wrong thing — the second page of
 *    warranties would simply not appear.
 * 2. **Counts come from `facets`, never from the rendered array.** A count taken from what is on
 *    screen describes the current filter, which is the one number the user already knows.
 * 3. **The counts do not move as you filter.** They are over the unfiltered set by design, so
 *    "Warranty 5" means five warranties exist, not five within the current selection.
 *
 * Within a facet, values are OR-ed (`status=overdue&status=due_soon` is either); across facets they
 * are AND-ed (`category=maintenance&display_category=Routine` is routine maintenance). That is the
 * standard faceted contract and the endpoint implements it — every facet param below takes an array.
 */
export interface PlanFilters {
	// TODO(plan-api-warranty-related): `category=warranty` returns warranty cards, not maintenance
	// obligations linked to keeping cover valid. A true warranty rollup needs a relationship-backed
	// query and facet; combining `category` and `display_category` cannot express that OR.
	/** Broad rollups. `maintenance` intentionally contains both routine and manufacturer work. */
	category: PlanCategory[];
	/** The card's visible type, offered as a refinement beneath the broad category rollup. */
	displayCategory: PlanDisplayCategory[];
	status: PlanStatus[];
	/** Where the card came from — a document Bundle read, the system, or the household itself. */
	sourceOrigin: PlanSourceOrigin[];
	/** The building system a card belongs to. Twenty-four possible values, so it is not a pill row. */
	systemDomain: SystemDomain[];
	search?: string;
}

/** The facet selections alone, without the free-text search that travels beside them. */
export type PlanFilterSelection = Omit<PlanFilters, 'search'>;

export const EMPTY_PLAN_FILTERS: PlanFilterSelection = {
	category: [],
	displayCategory: [],
	status: [],
	sourceOrigin: [],
	systemDomain: [],
};

/**
 * Filters as the endpoint's query, with empty selections omitted.
 *
 * An empty array is not the same as an absent param — sending `display_category=[]` asks for the
 * cards in no display category, which is none of them.
 *
 * `expired` needs `include_expired_warranties` sent with it, and `status=expired` alone is a query
 * that cannot match. The flag defaults to false and is applied *before* the status filter, so the
 * expired rows are gone by the time status is considered — while `facets.by_status` still counts
 * them, because facets are over the unfiltered set. Asking for expired without the flag therefore
 * offers a count of 1 and returns nothing. Selecting the status is the user saying they want those
 * rows, so the flag goes with it.
 */
export const planFilterQuery = (filters: PlanFilters) => ({
	...(filters.category.length ? { category: filters.category } : {}),
	...(filters.displayCategory.length ? { display_category: filters.displayCategory } : {}),
	...(filters.status.length ? { status: filters.status } : {}),
	...(filters.sourceOrigin?.length ? { source_origin: filters.sourceOrigin } : {}),
	...(filters.systemDomain?.length ? { system_domain: filters.systemDomain } : {}),
	...(filters.status.includes('expired') ? { include_expired_warranties: true } : {}),
	...(filters.search ? { search: filters.search } : {}),
});

/** URL round-tripping. One comma-separated param per facet keeps a shared link readable. */
export const parseFilterParam = <T extends string>(value: string | undefined): T[] =>
	value ? (value.split(',').filter(Boolean) as T[]) : [];

export const toFilterParam = (values: readonly string[]): string | undefined =>
	values.length ? values.join(',') : undefined;

export interface FacetOption<T extends string> {
	value: T;
	count: number;
}

/**
 * The values a facet actually has, with their counts, in the order the backend listed them.
 *
 * Driven by the response rather than by a hardcoded list: a facet key the plan never returns is a
 * filter that would always come back empty, and offering it is how a control ends up saying zero.
 */
export const facetOptions = <T extends string>(counts: Record<string, number> | undefined): FacetOption<T>[] =>
	Object.entries(counts ?? {})
		.filter(([, count]) => count > 0)
		.map(([value, count]) => ({ value: value as T, count }));

/** Total across a facet — what an "All" control counts, and it must come from the same facet. */
export const facetTotal = (counts: Record<string, number> | undefined): number =>
	Object.values(counts ?? {}).reduce((total, count) => total + count, 0);

export const statusFacetOptions = (facets: ScheduleFacetsOut | undefined) =>
	facetOptions<PlanStatus>(facets?.by_status);

export const categoryFacetOptions = (facets: ScheduleFacetsOut | undefined) =>
	facetOptions<PlanCategory>(facets?.by_category);

export const displayCategoryFacetOptions = (facets: ScheduleFacetsOut | undefined) =>
	facetOptions<PlanDisplayCategory>(facets?.by_display_category);

export const sourceOriginFacetOptions = (facets: ScheduleFacetsOut | undefined) =>
	facetOptions<PlanSourceOrigin>(facets?.by_source_origin);

export const systemDomainFacetOptions = (facets: ScheduleFacetsOut | undefined) =>
	facetOptions<SystemDomain>(facets?.by_system_domain);
